import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Film, Users, Award, Calendar, ChevronRight, Star, CheckCircle } from 'lucide-react';

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_set-roster/artifacts/4vngv49q_2ndTeamStandinsLogoWhite.png" 
              alt="2nd Team Casting" 
              className="h-28 w-auto"
            />
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#services" className="text-white/70 hover:text-white transition-colors">Services</a>
            <Link to="/training" className="text-white/70 hover:text-white transition-colors">Training</Link>
            <a href="#about" className="text-white/70 hover:text-white transition-colors">About</a>
            <Link to="/chit-calculator" className="text-white/70 hover:text-white transition-colors">Chit Calculator</Link>
          </div>
          
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-white/70 hover:text-white" data-testid="nav-login-btn">
                Login
              </Button>
            </Link>
            <Link to="/register">
              <Button className="gold-gradient text-black font-semibold hover:brightness-110 transition-all" data-testid="nav-register-btn">
                Join Us
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section — Camera operator at sunset */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <img 
          src="https://customer-assets.emergentagent.com/job_eb07d9f1-df0a-45f8-8183-e35769881cf0/artifacts/o3o6zpxq_IMG20251215145654~2.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.2) saturate(0.7)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-[#050505]" />
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8 backdrop-blur-sm">
            <Star className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-sm text-white/70">UK's Premier Stand-In Agency</span>
          </div>
          
          <h1 className="font-['Playfair_Display'] text-5xl md:text-7xl lg:text-8xl font-bold mb-6 leading-tight">
            <span className="text-white">Stand-In &</span><br />
            <span className="gold-text">Picture Double</span><br />
            <span className="text-white">Specialists</span>
          </h1>
          
          <p className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto mb-12">
            2nd Team is a dedicated Stand-in and Picture Double casting agency.
            We supply professionally trained, set-ready Stand-ins to the UK's biggest productions.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register?role=production">
              <Button 
                className="gold-gradient text-black font-bold text-lg px-8 py-6 uppercase tracking-wider hover:brightness-110 transition-all"
                data-testid="hero-find-standin-btn"
              >
                Find a Stand-In
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/register?role=standin">
              <Button 
                variant="outline"
                className="border-[#D4AF37] text-[#D4AF37] font-semibold text-lg px-8 py-6 uppercase tracking-wider hover:bg-[#D4AF37]/10 transition-all"
                data-testid="hero-join-btn"
              >
                Apply to Join
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Services Section — Canal architecture */}
      <section id="services" className="relative py-32 px-6 overflow-hidden">
        <img 
          src="https://customer-assets.emergentagent.com/job_eb07d9f1-df0a-45f8-8183-e35769881cf0/artifacts/fgx58496_IMG20251130150934.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.3) saturate(0.6)' }}
        />
        <div className="absolute inset-0 bg-black/60" />

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold mb-4">
              <span className="gold-text">Our Services</span>
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              Professional stand-in services for film and television productions across the UK
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="group bg-black/50 backdrop-blur-sm border border-white/5 rounded-2xl p-8 hover:border-[#D4AF37]/30 transition-colors">
              <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-6 group-hover:bg-[#D4AF37]/20 transition-colors">
                <Users className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h3 className="font-['Playfair_Display'] text-2xl font-bold text-white mb-4">
                Stand-In Casting
              </h3>
              <p className="text-white/60 mb-6">
                We introduce Stand-ins, Picture Doubles, and Featured Artists for roles in film, television and media. Our members are dedicated professionals.
              </p>
              <Link to="/register?role=production" className="text-[#D4AF37] font-semibold flex items-center gap-2 hover:gap-3 transition-all">
                Request an Artist <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="group bg-black/50 backdrop-blur-sm border border-white/5 rounded-2xl p-8 hover:border-[#D4AF37]/30 transition-colors">
              <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-6 group-hover:bg-[#D4AF37]/20 transition-colors">
                <Award className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h3 className="font-['Playfair_Display'] text-2xl font-bold text-white mb-4">
                Professional Training
              </h3>
              <p className="text-white/60 mb-6">
                The sole provider of professional training for Stand-ins and Picture Doubles in the UK. Get set-ready with our industry-endorsed program.
              </p>
              <Link to="/register?role=standin" className="text-[#D4AF37] font-semibold flex items-center gap-2 hover:gap-3 transition-all">
                Apply Here <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="group bg-black/50 backdrop-blur-sm border border-white/5 rounded-2xl p-8 hover:border-[#D4AF37]/30 transition-colors">
              <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-6 group-hover:bg-[#D4AF37]/20 transition-colors">
                <Film className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h3 className="font-['Playfair_Display'] text-2xl font-bold text-white mb-4">
                Consistent Work
              </h3>
              <p className="text-white/60 mb-6">
                Our graduates are the most trusted and professional cast support crew in the industry. Join now for consistent work on major UK productions.
              </p>
              <Link to="/register?role=standin" className="text-[#D4AF37] font-semibold flex items-center gap-2 hover:gap-3 transition-all">
                Learn More <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-32 px-6 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mx-auto lg:mx-0">
            <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold mb-6">
              <span className="text-white">Why Choose</span><br />
              <span className="gold-text">2nd Team?</span>
            </h2>
            <p className="text-white/60 text-lg mb-10">
              We provide experienced professionals, support and expertise to ensure the perfect fit for any production.
            </p>
            
            <div className="space-y-6">
              {[
                'All stand-ins professionally trained and set-ready',
                'Advanced matching system for precise casting',
                'Secure, verified professionals with right-to-work',
                'Instant availability checking via WhatsApp & Email',
                'Automated invoicing and chit generation'
              ].map((feature, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  <span className="text-white/80">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Productions We've Worked With — Ornate studio */}
      <section className="relative py-20 px-6 overflow-hidden">
        <img 
          src="https://customer-assets.emergentagent.com/job_eb07d9f1-df0a-45f8-8183-e35769881cf0/artifacts/35w6msz2_IMG20260228095435.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.25) saturate(0.6)' }}
        />
        <div className="absolute inset-0 bg-black/60" />

        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <p className="text-[#D4AF37] text-sm uppercase tracking-[0.3em] mb-10 font-semibold">Productions We've Supplied Stand-Ins To</p>
          <div className="flex flex-wrap justify-center items-center gap-x-12 gap-y-6">
            {[
              'House of the Dragon',
              'Mobland',
              'The Magic Faraway Tree',
              'My Lady Jane',
              'Your Christmas or Mine 2'
            ].map((title, i) => (
              <span 
                key={i} 
                className="font-['Playfair_Display'] text-2xl md:text-3xl text-white/80 hover:text-white transition-colors"
                style={{ fontStyle: 'italic' }}
              >
                {title}
              </span>
            ))}
          </div>
          <div className="mt-8 w-24 h-px bg-[#D4AF37]/30 mx-auto" />
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-1 mb-8">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-5 h-5 fill-[#D4AF37] text-[#D4AF37]" />
            ))}
          </div>
          
          <blockquote className="font-['Playfair_Display'] text-2xl md:text-3xl text-white italic mb-8">
            "2nd Team's Stand-ins continue to be the most reliable and well trained 2nd team I can find. I have complete confidence in their people as they clearly receive excellent training and are genuinely set ready."
          </blockquote>
          
          <div className="text-[#D4AF37] font-semibold">
            — Senior Crowd 2nd, Major UK Production
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="about" className="py-24 px-6 bg-gradient-to-b from-[#0A0A0A] to-[#050505]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold mb-6">
            <span className="text-white">Ready to </span>
            <span className="gold-text">Get Started?</span>
          </h2>
          <p className="text-white/60 text-lg mb-10 max-w-2xl mx-auto">
            Whether you're a production looking for reliable stand-ins or a professional seeking consistent work in the UK film industry, we're here to help.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register?role=production">
              <Button 
                className="gold-gradient text-black font-bold px-8 py-6 text-lg uppercase tracking-wider hover:brightness-110 transition-all"
                data-testid="cta-production-btn"
              >
                I'm a Production
              </Button>
            </Link>
            <Link to="/register?role=standin">
              <Button 
                variant="outline"
                className="border-[#D4AF37] text-[#D4AF37] font-semibold px-8 py-6 text-lg uppercase tracking-wider hover:bg-[#D4AF37]/10 transition-all"
                data-testid="cta-standin-btn"
              >
                I'm a Stand-In
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_set-roster/artifacts/4vngv49q_2ndTeamStandinsLogoWhite.png" 
                alt="2nd Team Casting" 
                className="h-28 w-auto"
              />
            </div>
            
            <div className="flex items-center gap-8 text-white/50 text-sm">
              <span>Paddington, London</span>
              <span>•</span>
              <a href="mailto:info@2ndteam.co.uk" className="hover:text-white transition-colors">info@2ndteam.co.uk</a>
            </div>

            <div className="flex items-center gap-6 text-white/40 text-sm">
              <Link to="/terms" className="hover:text-white transition-colors">Terms & Conditions</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            </div>
            
            <div className="text-white/30 text-sm">
              © 2026 2nd Team Casting. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
